import { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { useEditorRef } from 'platejs/react';
import { toast } from 'react-toastify';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Button, Modal } from '../../../_custom';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import { docxFileToFragment } from '../../serializers/docxToPlate';
import { stripNewlinesFromFragment } from '../../serializers/pasteNormalize';
import { markdownToPlate } from '../../ai/markdownToPlate';
import { sanitizePlateFragment } from '../../serializers/sanitizePlateFragment';
import { IMPORT_ACCEPT, classifyImportFile } from './importFormats';
import styles from './ImportDocxButton.module.scss';

function ImportModeModal({
    open,
    onReplace,
    onAppend,
    onCancel,
}: {
    open: boolean;
    onReplace: () => void;
    onAppend: () => void;
    onCancel: () => void;
}) {
    return (
        <Modal open={open} onClose={onCancel} size="flexible" withoutCloseButton className={styles.OverDrawer}>
            <div className={styles.ImportModeModal}>
                <h3 className={styles.Title}>Import Document</h3>
                <p className={styles.Subtitle}>
                    Do you want to replace the entire document or append the imported content to the existing one?
                </p>
                <div className={styles.Actions}>
                    <Button text="Cancel" onClick={onCancel} variant="purpleinverted" style={{ marginTop: 0 }} />
                    <Button text="Append" onClick={onAppend} variant="purpleinverted" style={{ marginTop: 0 }} />
                    <Button text="Replace Document" onClick={onReplace} variant="purple" style={{ marginTop: 0 }} />
                </div>
            </div>
        </Modal>
    );
}

export function ImportDocxButton() {
    const editor = useEditorRef();
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [pendingFragment, setPendingFragment] = useState<any[] | null>(null);

    const onClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        inputRef.current?.click();
    }, []);

    const onFile = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (e.target) e.target.value = '';
            if (!file) return;

            const kind = classifyImportFile(file.name);
            // This button's own file input only advertises .md/.doc/.docx (IMPORT_ACCEPT
            // above) — 'html' is a different import surface (Reports' file-list import) —
            // so it's excluded here defensively rather than silently routed to docxFileToFragment.
            if (!kind || kind === 'html') {
                toast.error('Please choose a .md, .doc, or .docx file');
                return;
            }

            setBusy(true);
            try {
                let fragment: any[] = [];
                if (kind === 'md') {
                    const text = await file.text();
                    fragment = (markdownToPlate(editor, text) as any[]) ?? [];
                } else {
                    const result = await docxFileToFragment(editor, file);
                    fragment = result.fragment;
                }

                if (!fragment.length) {
                    toast.error("Couldn't read any content from that file");
                    return;
                }

                setPendingFragment(sanitizePlateFragment(fragment));
            } catch (err) {
                console.error('[ImportDocxButton] failed to import document', err);
                if (kind === 'doc') {
                    toast.error("Legacy .doc isn't supported — please re-save it as .docx and try again");
                } else {
                    toast.error('Failed to import the document');
                }
            } finally {
                setBusy(false);
            }
        },
        [editor],
    );

    const doAppend = useCallback(
        (fragment: any[]) => {
            const ed: any = editor;
            try {
                if (!ed.selection) {
                    const end = ed?.api?.end?.([]);
                    if (end) ed.tf.select(end);
                }
                ed?.tf?.focus?.();
            } catch {
                /* selection not ready */
            }
            ed?.tf?.insertFragment?.(stripNewlinesFromFragment(fragment));
        },
        [editor],
    );

    const doReplace = useCallback(
        (fragment: any[]) => {
            const ed: any = editor;
            ed.tf.withoutNormalizing(() => {
                for (let i = ed.children.length - 1; i >= 0; i--) {
                    ed.tf.removeNodes({ at: [i] });
                }
                ed.tf.insertNodes(stripNewlinesFromFragment(fragment), { at: [0] });
            });
            ed.tf.deselect?.();
        },
        [editor],
    );

    const handleReplace = useCallback(() => {
        if (!pendingFragment) return;
        doReplace(pendingFragment);
        setPendingFragment(null);
    }, [pendingFragment, doReplace]);

    const handleAppend = useCallback(() => {
        if (!pendingFragment) return;
        doAppend(pendingFragment);
        setPendingFragment(null);
    }, [pendingFragment, doAppend]);

    const handleCancel = useCallback(() => {
        setPendingFragment(null);
    }, []);

    return (
        <>
            <button
                type="button"
                className={clsx(toolbarStyles.btn)}
                onMouseDown={onClick}
                disabled={busy}
                title="Import document (Markdown or Word)"
                aria-label="Import document"
            >
                <FileUploadOutlinedIcon fontSize="small" /> Import
            </button>
            <input ref={inputRef} type="file" accept={IMPORT_ACCEPT} onChange={onFile} style={{ display: 'none' }} />
            <ImportModeModal open={pendingFragment !== null} onReplace={handleReplace} onAppend={handleAppend} onCancel={handleCancel} />
        </>
    );
}
